import { copy } from '../clipboard'
import { reportRecoverableError } from '../core/diagnostics'

export interface ShareOptions {
  title?: string
  text?: string
  url?: string
}

export type ShareChannel = 'native' | 'telegram' | 'twitter' | 'copy'

const SOCIAL_LINKS: Record<Exclude<ShareChannel, 'native' | 'copy'>, (o: Required<ShareOptions>) => string> = {
  telegram: o => `https://t.me/share/url?url=${encodeURIComponent(o.url)}&text=${encodeURIComponent(o.text)}`,
  twitter: o => `https://twitter.com/intent/tweet?text=${encodeURIComponent(o.text)}&url=${encodeURIComponent(o.url)}`,
}

function resolveOptions(options: ShareOptions): Required<ShareOptions> {
  return {
    title: options.title ?? document.title,
    text: options.text ?? '',
    url: options.url ?? location.href,
  }
}

/**
 * Shares via the Web Share API when available, otherwise opens a social
 * share link for the given channel, or copies the URL as a last resort.
 */
export async function share(options: ShareOptions = {}, channel?: ShareChannel): Promise<{ ok: boolean, via: ShareChannel | 'none' }> {
  const resolved = resolveOptions(options)

  if ((!channel || channel === 'native') && navigator.share) {
    try {
      await navigator.share(resolved)
      return { ok: true, via: 'native' }
    }
    catch (error: unknown) {
      reportRecoverableError('native sharing failed; applying the requested fallback', error)
      if (channel === 'native')
        return { ok: false, via: 'none' }
      // fall through to link/copy fallback
    }
  }

  if (channel && channel !== 'native' && channel !== 'copy') {
    const buildLink = SOCIAL_LINKS[channel]
    window.open(buildLink(resolved), '_blank', 'noopener,noreferrer')
    return { ok: true, via: channel }
  }

  const result = await copy(resolved.url)
  return { ok: result.ok, via: 'copy' }
}
